import React, {useEffect, useMemo, useState} from 'react';
import {Link as RouterLink, useNavigate, useSearchParams} from 'react-router-dom';
import {FilterInput} from '@interstate/components/FilterInput';
import {META, SUB_PROCESSORS, THIRD_PARTY_CATEGORIES, type SubProcessor} from '../mock/data';
import {useHealth} from '../context/HealthContext';

type Tab = 'third-party' | 'affiliate';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 50;

const SortIcon = ({active, dir}: {active: boolean; dir: SortDir}) =>
  <span className="ml-1 inline-flex flex-col gap-px">
    <span className={`w-0 h-0 border-x-[3px] border-x-transparent border-b-[4px] ${active && dir === 'asc' ? 'border-b-white' : 'border-b-primary-300'}`} />
    <span className={`w-0 h-0 border-x-[3px] border-x-transparent border-t-[4px] ${active && dir === 'desc' ? 'border-t-white' : 'border-t-primary-300'}`} />
  </span>
;

const formatDate = (isoDate: string): string => {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

function CategoryFilter({
  categories,
  selected,
  onSelect
}: {
  categories: string[];
  selected: string | null;
  onSelect: (cat: string | null) => void;
}): React.ReactElement {
  return (
    <div className="flex flex-wrap gap-2">
      <button
        onClick={() => onSelect(null)}
        className={[
          'px-3 py-1.5 rounded-full text-xs font-medium transition-colors border',
          selected === null ?
            'bg-primary-700 text-white border-primary-700' :
            'bg-white text-gray-600 border-gray-200 hover:border-primary-300 hover:text-primary-700'
        ].join(' ')}
      >
        All
      </button>
      {categories.map(cat =>
        <button
          key={cat}
          onClick={() => onSelect(selected === cat ? null : cat)}
          className={[
            'px-3 py-1.5 rounded-full text-xs font-medium transition-colors border',
            selected === cat ?
              'bg-primary-700 text-white border-primary-700' :
              'bg-white text-gray-600 border-gray-200 hover:border-primary-300 hover:text-primary-700'
          ].join(' ')}
        >
          {cat}
        </button>
      )}
    </div>
  );
}

function Pagination({
  currentPage,
  totalPages,
  totalRows,
  pageSize,
  onPage
}: {
  currentPage: number;
  totalPages: number;
  totalRows: number;
  pageSize: number;
  onPage: (page: number) => void;
}): React.ReactElement {
  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalRows);

  return (
    <div className="flex items-center justify-between mt-4 px-1">
      <span className="text-xs text-gray-500">
        Showing {start}–{end} of {totalRows}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPage(1)}
          disabled={currentPage === 1}
          className="px-2 py-1 text-xs rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          «
        </button>
        <button
          onClick={() => onPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-2 py-1 text-xs rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ‹
        </button>
        <span className="px-3 py-1 text-xs text-gray-700 font-medium">
          {currentPage} / {totalPages}
        </span>
        <button
          onClick={() => onPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-2 py-1 text-xs rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ›
        </button>
        <button
          onClick={() => onPage(totalPages)}
          disabled={currentPage === totalPages}
          className="px-2 py-1 text-xs rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          »
        </button>
      </div>
    </div>
  );
}

function SubProcessorTable({
  rows,
  sortDir,
  onSort,
  sortActive,
  showCategory
}: {
  rows: SubProcessor[];
  sortDir: SortDir;
  onSort: () => void;
  sortActive: boolean;
  showCategory: boolean;
}): React.ReactElement {
  return (
    <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
      {rows.length === 0 ?
        <div className="py-16 text-center">
            <p className="text-gray-400 text-sm">No sub-processors match your search.</p>
          </div> :
        <table className="w-full text-sm">
            <thead>
              <tr className="bg-primary-700">
                <th className="px-5 py-3 text-left w-64">
                  <button
                    onClick={onSort}
                    className="flex items-center text-xs font-semibold text-white uppercase tracking-wider hover:text-primary-100"
                  >
                    Name of Sub-processor
                    <SortIcon active={sortActive} dir={sortDir} />
                  </button>
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Description
                </th>
                {showCategory &&
                  <th className="px-5 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider w-48">
                    Category
                  </th>
                }
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map(sp =>
                <tr key={sp.id} className="hover:bg-blue-50/40 transition-colors">
                  <td className="px-5 py-4 align-top font-semibold text-gray-900 w-64">
                    {sp.name}
                  </td>
                  <td className="px-5 py-4 text-gray-700 leading-relaxed">
                    {sp.services}
                  </td>
                  {showCategory &&
                    <td className="px-5 py-4 align-top w-48">
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-100 whitespace-nowrap">
                        {sp.category}
                      </span>
                    </td>
                  }
                </tr>
              )}
            </tbody>
          </table>
      }
    </div>
  );
}

export default function SubProcessorList(): React.ReactElement {
  const navigate = useNavigate();
  const {isDown} = useHealth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: Tab = searchParams.get('tab') === 'affiliate' ? 'affiliate' : 'third-party';
  const [search, setSearch] = useState('');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [sortActive, setSortActive] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const allThirdParty = SUB_PROCESSORS.filter(sp => sp.type === 'third-party');
  const allAffiliates = SUB_PROCESSORS.filter(sp => sp.type === 'affiliate');

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedCategory, activeTab]);

  const handleSort = () => {
    if (!sortActive) {
      setSortActive(true);
      setSortDir('asc');
    } else {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    }
  };

  const applyFilters = (rows: SubProcessor[], withCategory: boolean): SubProcessor[] => {
    const term = search.toLowerCase();
    let result = rows;
    if (term) {
      result = result.filter(sp =>
        sp.name.toLowerCase().includes(term) ||
        sp.services.toLowerCase().includes(term)
      );
    }
    if (withCategory && selectedCategory) {
      result = result.filter(sp => sp.category === selectedCategory);
    }
    if (sortActive) {
      result = [...result].sort((a, b) =>
        sortDir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
      );
    }
    return result;
  };

  const filteredThirdParty = useMemo(
    () => applyFilters(allThirdParty, true),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search, selectedCategory, sortActive, sortDir]
  );

  const filteredAffiliates = useMemo(
    () => applyFilters(allAffiliates, false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search, sortActive, sortDir]
  );

  const activeRows = activeTab === 'third-party' ? filteredThirdParty : filteredAffiliates;
  const totalPages = Math.max(1, Math.ceil(activeRows.length / PAGE_SIZE));
  const pagedRows = activeRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const switchTab = (tab: Tab) => {
    setSearchParams(tab === 'third-party' ? {} : {tab});
    setSearch('');
    setSelectedCategory(null);
  };

  return (
    <div className="min-h-screen bg-subdued font-sans">

      {/* Page header */}
      <header className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-screen-xl mx-auto px-6 py-8">

          <div className="flex items-start justify-between gap-8 mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-1 h-8 bg-primary-700 rounded-full shrink-0" />
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                  Sub-processor List
                </h1>
              </div>
              <p className="text-xs text-gray-500 pl-4">
                Last Updated: {formatDate(META.lastUpdated)}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => navigate('/subscribe')}
                disabled={isDown}
                title={isDown ? 'Service temporarily unavailable' : undefined}
                className="px-4 py-2 rounded-md text-sm font-medium bg-primary-700 text-white hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-700"
              >
                Subscribe to updates
              </button>
              {isDown ?
                <span
                  title="Service temporarily unavailable"
                  className="px-4 py-2 rounded-md text-sm font-medium border border-gray-200 text-gray-400 cursor-not-allowed"
                >
                  Unsubscribe
                </span> :
                <RouterLink
                  to="/unsubscribe"
                  className="px-4 py-2 rounded-md text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 no-underline transition-colors"
                >
                  Unsubscribe
                </RouterLink>
              }
            </div>
          </div>

          <div className="space-y-4">
            <p className="text-sm text-gray-700 leading-relaxed">
              Cox Automotive, Inc. and its subsidiaries (each, a &ldquo;Cox Auto Entity&rdquo; and
              collectively, &ldquo;Cox Auto&rdquo;) may engage sub-processors to help deliver one or
              more of the services offered to our customers. Sub-processors may include third-party
              vendors as well as Cox Auto affiliates. This page lists current sub-processors engaged
              by one or more Cox Auto Entities in connection with the processing of personal data on
              behalf of a customer. A sub-processor may support one or more services offered by Cox
              Auto. Cox Auto will update this page as sub-processors are added or removed. To
              subscribe to updates, click{' '}
              <RouterLink
                to="/subscribe"
                className="text-primary-700 underline hover:text-primary-800"
              >
                here
              </RouterLink>
              .
            </p>
            <div className="space-y-2">
              <p className="text-sm text-gray-700 font-medium">
                For each sub-processor, we provide the following information:
              </p>
              <ul className="space-y-1">
                <li className="flex gap-2 text-sm text-gray-700">
                  <span className="text-primary-700 font-bold shrink-0">•</span>
                  <span>
                    <strong>Name of sub-processor.</strong>{' '}
                    The legal name of the entity engaged as a sub-processor.
                  </span>
                </li>
                <li className="flex gap-2 text-sm text-gray-700">
                  <span className="text-primary-700 font-bold shrink-0">•</span>
                  <span>
                    <strong>Description.</strong>{' '}
                    A description of the processing activities the sub-processor performs in support
                    of one or more services offered by one or more Cox Auto Entities.
                  </span>
                </li>
                <li className="flex gap-2 text-sm text-gray-700">
                  <span className="text-primary-700 font-bold shrink-0">•</span>
                  <span>
                    <strong>Category.</strong>{' '}
                    The functional category of the sub-processor.
                  </span>
                </li>
              </ul>
            </div>
          </div>

        </div>
      </header>

      {/* Tab navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-screen-xl mx-auto px-6">
          <nav className="flex" role="tablist" aria-label="Sub-processor sections">
            <TabButton
              active={activeTab === 'third-party'}
              count={allThirdParty.length}
              onClick={() => switchTab('third-party')}
            >
              Third-Party Sub-processors
            </TabButton>
            <TabButton
              active={activeTab === 'affiliate'}
              count={allAffiliates.length}
              onClick={() => switchTab('affiliate')}
            >
              Cox Auto Sub-processors
            </TabButton>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-screen-xl mx-auto px-6 py-6">

        {/* Category filter — third-party tab only */}
        {activeTab === 'third-party' &&
          <div className="mb-4">
            <CategoryFilter
              categories={THIRD_PARTY_CATEGORIES}
              selected={selectedCategory}
              onSelect={setSelectedCategory}
            />
          </div>
        }

        {/* Search + count row */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 max-w-md">
            <FilterInput
              name="search"
              label=""
              placeholder="Search by name or description..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {search &&
            <button
              onClick={() => setSearch('')}
              className="text-sm text-primary-700 hover:text-primary-800 font-medium shrink-0"
            >
              Clear
            </button>
          }
          <span className="text-sm text-gray-500 shrink-0 ml-auto">
            {activeRows.length !== (activeTab === 'third-party' ? allThirdParty.length : allAffiliates.length) ?
              <>{activeRows.length} of {activeTab === 'third-party' ? allThirdParty.length : allAffiliates.length} sub-processors</> :
              <>{activeRows.length} sub-processors</>
            }
          </span>
        </div>

        {/* Section description for affiliates */}
        {activeTab === 'affiliate' &&
          <p className="text-sm text-gray-600 leading-relaxed mb-4 max-w-3xl">
            The Cox Auto Entities listed below may also act as sub-processors to the extent they
            process personal data when providing technical and operational support services to
            customers.
          </p>
        }

        {/* Table */}
        <SubProcessorTable
          rows={pagedRows}
          sortDir={sortDir}
          onSort={handleSort}
          sortActive={sortActive}
          showCategory={activeTab === 'third-party'}
        />

        {/* Pagination */}
        {totalPages > 1 &&
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalRows={activeRows.length}
            pageSize={PAGE_SIZE}
            onPage={setCurrentPage}
          />
        }

      </div>
    </div>
  );
}

function TabButton({
  active,
  count,
  onClick,
  children
}: {
  active: boolean;
  count: number;
  onClick: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={[
        'relative px-5 py-4 text-sm font-medium transition-colors whitespace-nowrap',
        'focus:outline-none',
        active ?
          'text-primary-700 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary-700' :
          'text-gray-500 hover:text-gray-800'
      ].join(' ')}
    >
      {children}
      <span className={[
        'ml-2 text-xs font-semibold px-1.5 py-0.5 rounded-full',
        active ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'
      ].join(' ')}>
        {count}
      </span>
    </button>
  );
}
